import { Injectable, isDevMode } from "@angular/core";
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
} from "rxjs";
import { ReplaySubject } from "rxjs";
import { isEqualWith, cloneDeep } from "lodash-es";

import { Sample } from "../samples/sample";
import { Achievement } from "./achievements.data";
import { AlertService } from "../alerts/alerts.service";
import { AppService } from "../../app.service";
import { CompoundTree } from "./../../shared/compound/compound-tree";
import { RemoteDataService } from "./remote-data.service";
import { LocalDataService } from "./local-data.service";
import type { DataService, SamplesProgress, UserBuild } from "./data.service";
import { type SampleService } from "../samples/sample.service";
import { DataProvider } from "./data-provider";
import { SAMPLES } from "./samples.data";
import { AuthenticationService } from "../authentication/authentication.service";
import { AppUser } from "../authentication/app-auth-types";

import { console } from "#/log";

import firebase from "firebase/compat/app";
import { Chem } from "#/declarations";
import { CompoundRender } from "#/app/shared/compound/compound-render";
import { GroupedSamples, SampleGrouperSorter } from "./SampleGrouperSorter";
import { verifySyncData } from "./verifySyncData";
import { AnalyticsService } from "../analytics/analytics.service";

@Injectable()
export class AppDataService implements DataService {
  isAuthorized: boolean = false;
  user: AppUser | null = null;
  providers: Array<{ type: string; provider: DataProvider }>;
  private sampleId: string | null = null;

  private providerBuild: Observable<any> = EMPTY;
  private providerNotes: Observable<Object> = EMPTY;
  private providerRanges: Observable<Array<any>> = EMPTY;
  private progressSubject: ReplaySubject<SamplesProgress> =
    new ReplaySubject<SamplesProgress>(1);

  private progressSubscription: any = null;
  private currentProvider!: DataProvider;
  private currentProviderType!: string;
  private currentProgress?: SamplesProgress;

  /**
   * Subject for app-data's current-sample-ID, intended for debug-visualization purposes.
   *
   * If you want the current sample, {@link SampleService.sample} is authoritative.
   *
   * @see {@link currentSampleId}
   **/
  private _currentSampleId = new BehaviorSubject<string | null>(null);

  /**
   * app-data's current-sample-ID, intended for debug-visualization purposes.
   *
   * If you want the current sample, {@link SampleService.sample} is authoritative.
   **/
  public get currentSampleId() {
    return this._currentSampleId.asObservable();
  }

  constructor(
    private authService: AuthenticationService,
    private remoteData: RemoteDataService,
    private localData: LocalDataService,
    private alertService: AlertService,
    private appService: AppService,
    private analytics: AnalyticsService,
  ) {
    this.providers = [
      { type: "remote", provider: remoteData },
      { type: "local", provider: localData },
    ];
    this.init();
  }

  /**
   * Subscribes to the user's authentication status, and also the remote
   * provider's status so that it can sync when it comes back online.
   */
  init() {
    console.debug(`app-data.service: init()`);
    this.setProvider("local");
    this.authService.getAuth().onAuthStateChanged((user: AppUser | null) => {
      this.onAuthChange(user);
    });
    this.remoteData
      .availableStatus()
      .subscribe(this.onRemoteAvailableChange.bind(this));
    this.appService.appActive.subscribe((active) => {
      console.debug(
        "app-data: appActive changed",
        active,
        "remoteData isAvailable:",
        this.remoteData.isAvailable(),
      );
      this.onRemoteAvailableChange(active && this.remoteData.isAvailable());
    });
    this.progressSubject.subscribe((d) => {
      this.currentProgress = cloneDeep(d);
      console.debug("app-data: progress changed", this.currentProgress);
    });
  }

  /**
   * Gets the current user, if any
   */
  getUser(): firebase.User | null {
    return this.user as firebase.User | null;
  }

  /**
   * Get the build observable for the current sample
   */
  getCurrentBuild(): Observable<UserBuild> {
    return this.providerBuild;
  }

  /**
   * Get the ranges observable for the current sample
   */
  getCurrentRanges(): Observable<Array<Chem.Range>> {
    return this.providerRanges;
  }

  /**
   * Get the notes observable for the current sample
   */
  getCurrentNotes(): Observable<Object> {
    return this.providerNotes;
  }

  /**
   * Get the progress observable for all of the samples
   */
  getSamplesProgress(): Observable<SamplesProgress> {
    return this.progressSubject.asObservable();
  }

  /**
   * Observable for both samples progress and grouped/sorted samples collection
   */

  getSamplesGroupedAndProgress(): Observable<
    [SamplesProgress, GroupedSamples]
  > {
    return this.getSamplesProgress().pipe(
      // tap((progress) => console.debug(`home-sidebar progress`, progress)),
      filter((progress) => typeof progress === "object"),
      // tap((progress) => console.debug(`home-sidebar progress (filtered)`, { progress }) ),
      SampleGrouperSorter.groupSortWithProgress(),
      // tap(([progress, groupedSamples]) => console.debug(`home-sidebar groupSortWithProgress`, { progress, groupedSamples }) ),
    );
  }

  getCurrentProviderType(): string {
    return this.currentProviderType;
  }

  getThankYouSeen(): Promise<boolean> {
    return this.localData.getSeen();
  }

  setThankYouSeen() {
    this.localData.setSeen();
  }

  /**
   * Set the user ID in the data providers
   */
  async setUserId(uid: string | null) {
    console.log("app-data.service: App data service setting user id: ", uid);
    if (!uid && this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
    try {
      this.remoteData.setUserId(uid);
    } catch (error) {
      if (!isDevMode()) throw error;
      console.warn(
        `error: remoteData.setUserId(${JSON.stringify(uid)}):`,
        error,
      );
    }

    await this.localData.setUserId(uid);

    if (uid && this.currentProvider) {
      this.progressSubscription = this.currentProvider
        .getSamplesProgress()
        .subscribe((data) => {
          this.progressSubject.next(data);
        });
    }
  }

  /**
   * Set the progress for a sample in the data providers.
   */
  async setSampleProgress(sampleId: string, progress: number): Promise<void> {
    await this.callSetter("setSampleProgress", sampleId, progress);
  }

  /**
   * Sets the current sample for the app in both the data services, then
   * sets the subscriptions to the data.  If a null value is passed, then
   * there is no current sample and all existing subscriptions are cancelled.
   */
  setCurrentSample(sampleId: string | null) {
    this.sampleId = sampleId ?? null;
    this._currentSampleId.next(this.sampleId);
    console.debug(
      `app-data.service: setCurrentSample(id: '${sampleId ?? "[null]"})'`,
    );

    /**
     * This is a bit risky, because we don't await setCurrentSample
     * even though it's async.
     *
     * This (synchronous) sequencing:
     * - call localData.setCurrentSample()
     * - call setSampleSubscriptions() to subscribe to the
     *   local-data-provider's updated observables
     *
     * ... relies on localData.setCurrentSample() _synchronously_
     * re-creating its observables _before_ await()ing, i.e. yielding
     * execution back to this function.
     *
     * @see {LocalDataService.setCurrentSample}
     **/
    this.localData.setCurrentSample(sampleId);

    /**
     * don't try to set if remote data isn't yet available; will get set when available; @see {onRemoteAvailableChange}
     */
    if (this.remoteData.isAvailable())
      this.remoteData.setCurrentSample(sampleId);

    if (sampleId) {
      this.setSampleSubscriptions();
    } else {
      this.providerBuild = EMPTY;
      this.providerNotes = EMPTY;
      this.providerRanges = EMPTY;
    }
  }

  static areBuildsEqual(oldData: any, buildData: any): boolean {
    const renderTreeMismatch =
      buildData?.render &&
      oldData?.render &&
      !CompoundTree.isEqual(buildData.render, oldData.render);
    const buildDataMismatch = !isEqualWith(buildData, oldData);
    const completeFlagMismatch =
      buildData && oldData && buildData.complete !== oldData.complete;

    const isEqual = !(
      renderTreeMismatch ||
      buildDataMismatch ||
      completeFlagMismatch
    );

    // verbose.debug(`AppDataService.areBuildsEqual:`, {
    //   // oldData,
    //   // oldStructure: JSON.stringify(oldData?.render?.structure),
    //   // buildData,
    //   // buildStructure: JSON.stringify(buildData?.render?.structure),
    //   isEqual,
    //   renderTreeMismatch,
    //   buildDataMismatch,
    //   completeFlagMismatch,
    // });

    return isEqual;
  }

  /**
   * Compares a set of ranges to see if they are equal
   */
  static areRangesEqual(
    ranges1: Array<Partial<Chem.RangeGeneric>>,
    ranges2: Array<Partial<Chem.RangeGeneric>>,
  ): boolean {
    if ((ranges1 && !ranges2) || (!ranges1 && ranges2)) {
      return false;
    }
    if (ranges1.length === ranges2.length) {
      for (let i = 0; i < ranges1.length; i++) {
        if (
          !isEqualWith(ranges1[i].assigned, ranges2[i].assigned) ||
          ranges1[i]["$key"] !== ranges2[i]["$key"] ||
          ranges1[i]["correct"] !== ranges2[i]["correct"] ||
          ranges1[i]["hydrogens"] !== ranges2[i]["hydrogens"] ||
          ranges1[i]["splitting"] !== ranges2[i]["splitting"]
        ) {
          return false;
        }
      }
      return true;
    }
    return false;
  }

  /**
   * Sets subscriptions for the current sample using the available provider
   * The subscription handler checks if the values are different before
   * updating the observable values.
   */
  setSampleSubscriptions() {
    console.log(
      "app-data.service: setSampleSubscriptions()",
      this.getCurrentProviderType(),
      this.currentProvider,
    );

    this.setCurrentProviderSubscriptions();
  }

  /**
   * Sets subscriptions for the current sample using the available provider
   * The subscription handlers checks if the values are different before
   * updating the observable values.
   */
  private setCurrentProviderSubscriptions() {
    const emptyUserBuild = {
      complete: false,
      render: CompoundRender.EMPTY,
    } as const;
    this.providerBuild = this.currentProvider.getCurrentBuild().pipe(
      // tap((data) =>
      //   verbose.debug(
      //     `app-data.service:observables:build: [1] pre-default:`,
      //     data?.render?.structure ?? data,
      //   ),
      // ),
      /** Handle empty observable */
      defaultIfEmpty(emptyUserBuild),
      /** Handle observable value with null/uninitialized .render */
      map((build) => {
        return build?.render ? build : emptyUserBuild;
      }),
      // tap((data) =>
      //   verbose.debug(
      //     `app-data.service:observables:build: [2] pre-distinct:`,
      //     data?.render?.structure ?? data,
      //   ),
      // ),
      distinctUntilChanged(AppDataService.areBuildsEqual),
      // tap((data) =>
      //   verbose.debug(
      //     `app-data.service:observables:build: [3] post-distinct:`,
      //     data?.render?.structure ?? data,
      //   ),
      // ),
    );

    // Subscribe to provider's getCurrentRanges

    this.providerRanges = this.currentProvider
      .getCurrentRanges()
      .pipe(distinctUntilChanged(AppDataService.areRangesEqual));

    this.providerNotes = this.currentProvider.getCurrentNotes().pipe(
      distinctUntilChanged(isEqualWith),
      // this.notesAreNewOperator()
    );
  }

  /**
   * Set the current data provider to the `type` ("local" || "remote")
   */
  setProvider(type: string) {
    console.debug(`app-data.service: setProvider():`, type);
    this.currentProviderType = type;
    if (this.progressSubscription) {
      this.progressSubscription.unsubscribe();
    }
    const provider = this.getProvider(type);
    if (!provider) {
      throw new Error(`Provider not found for type: ${type}`);
    }
    this.currentProvider = provider;
    this.progressSubscription = this.currentProvider
      .getSamplesProgress()
      .subscribe((data) => {
        if (!isEqualWith(data, this.currentProgress, this.isProgressEqual)) {
          this.progressSubject.next(data);
        }
      });
    if (this.sampleId) {
      this.setSampleSubscriptions();
    }
  }

  /**
   * Set the build in the data providers.
   */
  async setBuild(buildData: UserBuild): Promise<void> {
    await this.callSetter("setBuild", buildData);
  }

  /**
   * Set the range in the data providers.
   */
  async setRange(rangeId: string, rangeData: Object): Promise<void> {
    await this.callSetter("setRange", rangeId, rangeData);
  }

  /**
   * Set the notes in the data providers.
   */
  async setNotes(notesData: Object): Promise<void> {
    await this.callSetter("setNotes", notesData);
  }

  /**
   * Sends feedback in the data providers.
   */
  async sendFeedback(feedbackData: Object): Promise<void> {
    await this.callSetter("sendFeedback", feedbackData);
  }

  /**
   * Update the build in the data providers.
   */
  async updateBuild(buildData: Partial<UserBuild>): Promise<void> {
    console.debug(`app-data.service: updateBuild:`, buildData);
    await this.callSetter("updateBuild", buildData);
  }

  /**
   * Update the range in the data providers.
   */
  async updateRange(rangeId: string, rangeData: Object): Promise<void> {
    console.debug(`app-data.service: updateRange:`, { rangeId, rangeData });
    await this.callSetter("updateRange", rangeId, rangeData);
  }

  /**
   * Updates an achievement in local storage
   * @param achievement - a single achievement to update
   * @param value - the progress value to set to the achievement
   */
  async updateAchievements(
    achievement: Object | undefined,
    percent: number,
    progress: number,
  ): Promise<boolean> {
    const didCompleteAchievement = await this.localData.setAchievement(
      achievement,
      percent,
      progress,
    );
    return didCompleteAchievement;
  }

  /**
   * Sets ALL achievements in local memory
   */
  setAchievements(achievements: any): Promise<any> {
    let promises: Array<Promise<any>> = [];
    for (let achievement of achievements) {
      promises.push(this.localData.setAchievement(achievement));
    }
    return Promise.all(promises);
  }

  /**
   * If there is local achievement data, fetch it.
   * Only local storage fetching supported.
   */
  getLocalAchievements(ids: string[]): Promise<Achievement[]> {
    return this.localData.getAchievements(ids);
  }

  /**
   * Resets just the achievements
   */
  resetAchievements(): Promise<any> {
    return this.localData.resetAchievements();
  }

  /**
   * Saves a user's ability to have access to all samples after purchasing
   * (only for future opening of this app on this device; other devices will have to
   * use the 'restore purchase' option)
   */
  async setFullAccess(): Promise<void> {
    await this.localData.setUserAsAllAccess();
    //broadcast the variable to auth service
    this.authService.provideFullAccess(true);
  }

  /**
   * Gets the locally stored value for if the app should be fully accessible or not
   */
  getFullAccess(): Promise<Object> {
    return this.localData.getUserAllAccess();
  }

  /**
   * Resets the data in the available providers
   */
  async reset(): Promise<void> {
    this.progressSubject.next({});
    await this.callSetter("reset");
  }

  /**
   * Triggers the providers to remove subscriptions because there is no
   * longer a user.
   */
  clearUser() {
    this.providers.forEach((p) => {
      p.provider.setUserId("");
    });
  }

  getSampleMetadata(sampleId: string): Sample | null {
    return SAMPLES.find(({ $key }) => $key === sampleId) ?? null;
  }

  /**
   * Syncs local provider data to remote provider
   */
  async syncLocalToRemote(timestamp: number): Promise<void> {
    const data = await this.localData.getSyncData();
    await this.remoteData.setSyncData(data, timestamp);
    this.remoteData.setCurrentSample(this._currentSampleId.getValue());

    /** verify sync data */
    if (AppDataService.SHOULD_VERIFY_SYNC) {
      await verifySyncData(this.remoteData, data, timestamp, {
        fn: "syncLocalToRemote",
        source: "local",
        target: "local",
      });
    }
  }

  /**
   * Syncs remote provider data to local provider
   */
  async syncRemoteToLocal(timestamp: number): Promise<void> {
    try {
      const [remoteData] = await Promise.all([
        this.remoteData.getSyncData(),
        this.localData.reset(),
      ]);
      await this.localData.setSyncData(remoteData, timestamp);
      /** verify sync data */
      if (AppDataService.SHOULD_VERIFY_SYNC) {
        await verifySyncData(this.localData, remoteData, timestamp, {
          fn: "syncRemoteToLocal",
          source: "remote",
          target: "local",
        });
      }
    } catch (error) {
      console.error(error);
      return;
    }
  }

  /**
   * Handler that is triggered when the user comes back online.  The
   * timestamps on the providers are compared, and if the remote provider
   * is out of sync, then the data from local storage is sent.
   *
   * If the local data is out of sync (older timestamp than remote), the
   * user is prompted to confirm updating local data from remote.
   */
  async checkSync(): Promise<
    { outOfSync: string; timestamp: number } | { outOfSync: false }
  > {
    // check if data is in sync and update if not
    let remoteTime = this.remoteData.getLastUpdated();
    let localTime = this.localData.getLastUpdated();
    let outOfSync = this.isOutOfSync(remoteTime, localTime);
    if (outOfSync === "local") {
      console.debug("app-data: local data out of sync", localTime, remoteTime);
      // no local data, pull in from remote
      if (!localTime) {
        this.analytics.trackDataSync(outOfSync, "automatic");
        return {
          outOfSync,
          timestamp: remoteTime,
        };
      }
      // local data exists but is out of sync, prompt user
      const confirmed =
        await this.alertService.showConfirmAlert("confirm_sync");
      if (confirmed) {
        this.analytics.trackDataSync(outOfSync, "interactive-confirmed");
        return { outOfSync, timestamp: remoteTime };
      }
      this.analytics.trackDataSync(outOfSync, "interactive-confirmed");
      return { outOfSync: false };
    } else if (outOfSync === "remote") {
      console.debug("app-data: remote data is out of date");
      this.analytics.trackDataSync(outOfSync, "automatic");
      return {
        outOfSync,
        timestamp: localTime,
      };
    }

    console.debug("app-data: data is in sync", localTime, remoteTime);
    return { outOfSync: false };
  }

  /**
   * Checks if remote / local data is out of sync
   */
  private isOutOfSync(
    remoteTime: number,
    localTime: number,
  ): false | "local" | "remote" {
    let threshold = 120; // threshold of 120 seconds between local and remote
    if (remoteTime && !localTime) {
      return "local";
    }
    if (remoteTime && localTime) {
      let difference = Math.abs(remoteTime - localTime);
      if (difference > threshold) {
        return remoteTime < localTime ? "remote" : "local";
      }
    }
    return false;
  }

  /**
   * Checks if the progress arrays are equal
   */
  private isProgressEqual(p1: Array<any>, p2: Array<any>) {
    if ((p1 && !p2) || (!p1 && p2) || p1.length !== p2.length) {
      return false;
    }
    for (let i = 0; i < p1.length; i++) {
      if (p1[i].progress !== p2[i].progress) {
        return false;
      }
    }
    return true;
  }

  /**
   * Gets a data provider based on the type passed.
   */
  private getProvider(type: string) {
    for (let i = 0; i < this.providers.length; i++) {
      if (this.providers[i].type === type) {
        return this.providers[i].provider;
      }
    }
    return null;
  }

  /**
   * Calls the data provider functions for the passed setter, and sets the
   * provider with the last time it was updated.
   */
  private async callSetter<Setter extends keyof MemberFunctions<DataProvider>>(
    setter: Setter,
    ...args: Parameters<DataProvider[Setter]>
  ) {
    // console.log(this.user);
    if (!this.user) {
      throw new Error("no authenticated user when calling " + setter);
    }
    let timestamp = AppDataService.generateTimestamp();

    await Promise.all(
      this.providers.map(async (p) => {
        const setterFn = p.provider[
          setter
        ] as MemberFunctions<DataProvider>[Setter];

        if (typeof setterFn === "function") {
          if (p.provider.isAvailable()) {
            console.debug(`app-data: calling ${p.type} setter`, setter, args);
            try {
              await p.provider.setLastUpdated(timestamp);
              await (setterFn as any).apply(p.provider, args);
            } catch (error) {
              if (error instanceof Error) {
                error.message =
                  `provider '${p.type}' setter '${setter}': ` + error.message;
              }
              throw error;
            }
          } else {
            console.debug("app-data: provider not available: ", p.type);
          }
        } else {
          throw new Error("provider does not implement function: " + setter);
        }
      }),
    );
  }

  /**
   * Generates a timestamp with the current time.
   */
  public static generateTimestamp(): number {
    return Math.floor(Date.now() / 1000);
  }

  /**
   * Handler for when authentication state changes.  Sets the user ID for the
   * providers to get appropriate data.
   */
  private onAuthChange(user: AppUser | null): void {
    console.debug(
      `app-data onAuthChange(): received user '${AuthenticationService.debugUser(user)}'`,
    );
    if (user) {
      this.user = user;
      this.isAuthorized = true;
      this.setUserId(user.uid);
    } else {
      this.user = null;
      this.isAuthorized = false;
      this.setUserId(null);
    }
  }

  /**
   * Called when the availability of the remote data provider changes.  If it
   * is available, the data is synced to the remote provider.
   */
  private async onRemoteAvailableChange(isReady: boolean): Promise<void> {
    console.debug(`app-data.service: onRemoteAvailableChange:`, isReady);
    if (isReady && this.user) {
      /**
       * deferred initialization of current sample
       * @see {setCurrentSample}
       */
      this.remoteData.setCurrentSample(this.sampleId ?? null);

      const sync = await this.checkSync();

      if (sync.outOfSync) {
        switch (sync.outOfSync) {
          case "remote":
            console.debug("app-data: syncing to remote");
            await this.syncLocalToRemote(sync.timestamp);

            break;
          case "local":
            console.debug("app-data: syncing to local");
            await this.syncRemoteToLocal(sync.timestamp);
            break;
        }
      }
    }
  }

  private static get SHOULD_VERIFY_SYNC(): boolean {
    return AppService.buildEnv() !== "prod";
  }
}

/**
 * Utility mapping type; synthesize prototypes with `this` parameter
 */
type MemberFunctions<Type extends object> = {
  [Fn in keyof Type as Type[Fn] extends (...args: any[]) => any
    ? Fn
    : never]: Type[Fn] extends (...args: any[]) => any
    ? (this: Type, ...args: Parameters<Type[Fn]>) => ReturnType<Type[Fn]>
    : never;
};
